import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listWarehouses, createWarehouse, updateWarehouse, deleteWarehouse, type Warehouse } from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import { useSiteStore } from "../stores/siteStore";
import css from "./WarehouseAdminPage.module.css";

export function WarehouseAdminPage() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const qc = useQueryClient();
  const [name, setName] = useState("");

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses", siteId],
    queryFn: () => listWarehouses(siteId),
  });

  const create = useMutation({
    mutationFn: (body: { name: string }) => createWarehouse(siteId, body),
    onSuccess: () => {
      setName("");
      qc.invalidateQueries({ queryKey: ["warehouses", siteId] });
      // Refresh the store so the shell leaves setup and /live becomes the
      // landing once the first warehouse exists (ADR 175).
      void useSiteStore.getState().loadAvailableWarehouses(siteId);
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateWarehouse(siteId, id, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses", siteId] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteWarehouse(siteId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses", siteId] }),
  });

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div>
          <div className={css.title}>{t("warehousesAdmin.title")}</div>
          <div className={css.siteTag}>{siteId}</div>
        </div>
      </div>

      <form
        className={css.form}
        onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate({ name: name.trim() }); }}
      >
        <div className={css.field}>
          <label className={css.label} htmlFor="wh-name">{t("warehousesAdmin.new")}</label>
          <input id="wh-name" className={css.input} value={name} onChange={(e) => setName(e.target.value)}
                 placeholder={t("warehousesAdmin.newPlaceholder")} />
        </div>
        <button type="submit" className={css.btn} disabled={!name.trim() || create.isPending}>
          {create.isPending ? t("common.creating") : t("common.create")}
        </button>
      </form>

      <table className={css.table}>
        <thead>
          <tr><th>{t("common.col.id")}</th><th>{t("common.col.name")}</th><th>{t("warehousesAdmin.col.model")}</th><th style={{ width: 200 }}>{t("common.col.actions")}</th></tr>
        </thead>
        <tbody>
          {warehouses.map((w) => (
            <WarehouseRow
              key={w.id}
              warehouse={w}
              onRename={(n) => rename.mutate({ id: w.id, name: n })}
              onDelete={() => { if (confirm(t("warehousesAdmin.confirmDelete", { name: w.name }))) del.mutate(w.id); }}
            />
          ))}
          {warehouses.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: "center", padding: 24, color: "var(--op-ink-500)" }}>
              {t("warehousesAdmin.empty")}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function WarehouseRow({ warehouse, onRename, onDelete }: {
  warehouse: Warehouse;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(warehouse.name);

  return (
    <tr>
      <td>{warehouse.id}</td>
      <td>
        {editing ? (
          <input className={css.input} value={name} onChange={(e) => setName(e.target.value)}
                 onBlur={() => { setEditing(false); if (name !== warehouse.name && name.trim()) onRename(name.trim()); }}
                 onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                 autoFocus />
        ) : (
          <Link className={css.nameLink} to={`/admin/warehouses/${warehouse.id}`}>{warehouse.name}</Link>
        )}
      </td>
      <td>
        {warehouse.modelKey ? (
          <span className={css.modelOk}>{t("warehousesAdmin.modelOk")}</span>
        ) : (
          <span className={css.modelMissing}>{t("warehousesAdmin.modelMissing")}</span>
        )}
      </td>
      <td className={css.actions}>
        <button type="button" className={css.linkBtn} onClick={() => setEditing(true)}>
          {t("common.rename")}
        </button>
        <button type="button" className={css.dangerBtn} onClick={onDelete}>{t("common.delete")}</button>
      </td>
    </tr>
  );
}
